import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { firstValueFrom } from 'rxjs';

interface JiraSettings {
  baseUrl: string;
  email: string;
  apiToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class JiraService {
  private readonly JIRA_SETTINGS_PATH = 'settings/jira';
  private settings: JiraSettings | null = null;
  private readonly FUNCTION_URL = 'https://us-central1-premiergr-d8d90.cloudfunctions.net/createJiraIssue';

  constructor(
    private http: HttpClient,
    private firestore: AngularFirestore
  ) {
    // Initialize settings
    this.loadSettings().catch(error => {
      console.error('Failed to load Jira settings:', error);
    });
  }

  private async loadSettings(): Promise<JiraSettings | null> {
    console.log('Loading Jira settings...');
    const settingsDoc = await firstValueFrom(this.firestore.doc<JiraSettings>(this.JIRA_SETTINGS_PATH).get());
    console.log('Settings doc exists:', settingsDoc.exists);
    if (settingsDoc.exists) {
      const data = settingsDoc.data() as JiraSettings;
      console.log('Loaded Jira settings:', { ...data, apiToken: '[REDACTED]' });
      this.settings = data;
      return data;
    }
    return null;
  }

  async createIssue(issueData: any): Promise<any> {
    try {
      console.log('Creating Jira issue...');
      if (!this.settings) {
        console.log('No settings found, attempting to load...');
        const loadedSettings = await this.loadSettings();
        if (!loadedSettings) {
          throw new Error('No Jira credentials configured. Please have an admin set up the integration.');
        }
      }

      // Call the Firebase Function HTTP endpoint
      console.log('Calling createJiraIssue function with data:', issueData);
      
      const response = await firstValueFrom(this.http.post(
        this.FUNCTION_URL,
        issueData,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json'
          })
        }
      ));
      
      console.log('Jira issue created successfully:', response);
      return response;
    } catch (error: any) {
      console.error('Error creating Jira issue:', error);
      if (error.message?.includes('No Jira credentials configured')) {
        throw error;
      }
      throw new Error('Failed to create Jira issue. Please contact your administrator.');
    }
  }

  async updateJiraSettings(email: string, apiToken: string, baseUrl: string): Promise<void> {
    try {
      console.log('Updating Jira settings...');
      // Remove any trailing slashes from baseUrl and ensure https://
      const cleanBaseUrl = `https://${baseUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')}`;
      console.log('Clean base URL:', cleanBaseUrl);
      
      const settings: JiraSettings = {
        email,
        apiToken,
        baseUrl: cleanBaseUrl
      };
      
      // Test the credentials before saving
      const headers = new HttpHeaders({
        'Authorization': `Basic ${btoa(`${email}:${apiToken}`)}`,
        'Accept': 'application/json',
        'X-Atlassian-Token': 'no-check'
      });

      try {
        console.log('Testing Jira credentials...');
        await firstValueFrom(this.http.get(
          `${cleanBaseUrl}/rest/api/2/myself`,
          { headers }
        ));
        console.log('Credentials test successful');
      } catch (error) {
        console.error('Failed to validate Jira credentials:', error);
        throw new Error('Invalid Jira credentials. Please check your email and API token.');
      }

      await this.firestore.doc(this.JIRA_SETTINGS_PATH).set(settings);
      this.settings = settings;
      console.log('Jira settings updated successfully');
    } catch (error) {
      console.error('Error updating Jira settings:', error);
      throw error;
    }
  }
} 